<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import IconSearch from '~icons/carbon/search';
import IconClose from '~icons/carbon/close';

const props = defineProps<{
  searchFn?: () => void;
  modelValue: string;
  isOpen: boolean;
  placeholder?: string;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', modelValue: string): void;
  (e: 'update:isOpen', isOpen: boolean): void;
}>();

const { t } = useI18n();
const searchInput = ref<HTMLInputElement>();

function onInput(event: Event) {
  if (event.target instanceof HTMLInputElement) {
    emit('update:modelValue', event.target.value);
    props.searchFn && props.searchFn();
  }
}

function onToggleSearch(status: boolean) {
  emit('update:isOpen', status);

  if (status) {
    nextTick(() => {
      searchInput.value?.focus();
    });
  } else {
    // Reset the search query when the search input is closed.
    emit('update:modelValue', '');
    props.searchFn && props.searchFn();
  }
}
</script>

<template>
  <div class="flex" :class="[isOpen ? 'absolute inset-0' : '']">
    <div class="relative w-full" :class="[isOpen ? '' : 'hidden']">
      <IconSearch
        class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-new-gray-900 dark:text-white"
        aria-hidden="true"
      />
      <input
        id="posts-search"
        ref="searchInput"
        :value="modelValue"
        type="search"
        :placeholder="placeholder || `${t('general.search_placeholder')}...`"
        autocomplete="off"
        autocapitalize="off"
        autocorrect="off"
        spellcheck="false"
        aria-label="Search your projects"
        class="h-full w-full rounded-full border border-new-gray-100 bg-white py-1.5 pl-9 pr-3 transition-colors placeholder:text-sm focus:bg-white focus:outline-none dark:border-new-gray-900 dark:bg-gray-650 focus:dark:bg-gray-700"
        @input="onInput"
      />
    </div>

    <button class="flex w-8 flex-none items-center justify-center" @click="onToggleSearch(!isOpen)">
      <IconClose
        v-if="isOpen"
        class="text-xl text-new-gray-900 dark:text-white/50"
        aria-hidden="true"
      />
      <IconSearch
        v-else
        class="text-base text-new-gray-900 dark:text-white/50"
        aria-hidden="true"
      />
    </button>
  </div>
</template>

<style scoped lang="postcss"></style>
